import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Search, Download, X, ChevronDown, ChevronRight, Loader2, Copy, RotateCcw, Plus } from 'lucide-react';
import { apiService } from '../config/api';
import CSVExportModal from '../components/CSVExportModal';
import Portal from '../components/Portal';
import SmartPagination, { PAGE_SIZE_OPTIONS } from '../components/SmartPagination';
import { useAction } from '../contexts/ActionContext';

const DEFAULT_PAGE_SIZE = 25;
const STATUSES = ['Valid', 'Invalid', 'Catch All', 'Unknown', 'Disposable', 'Role Account', 'Free Account', 'Skipped', 'Syntax Error'];
const LIMIT_OPTIONS = [100, 500, 1000, 5000, 10000, 50000, 100000];

function statusBadge(s) {
    const m = {
        Valid: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
        Invalid: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
        'Catch All': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
        Disposable: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
        Unknown: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
        'Role Account': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
        'Free Account': 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
        Skipped: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
        'Syntax Error': 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300',
    };
    return m[s] || 'bg-gray-100 text-gray-600';
}

function safety(s) {
    if (s === 'Valid') return ['Safe', 'text-green-600'];
    if (s === 'Catch All') return ['Risky', 'text-yellow-600'];
    return ['Unsafe', 'text-red-500'];
}

function humanize(k) { return k.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, c => c.toUpperCase()); }

function categorizeKey(k) {
    const rules = [
        ['Job Title & Role', /job.?title|^title$|role|position|seniority|function|department/i],
        ['Contact Info', /phone|mobile|tel|linkedin|twitter|facebook|social|profile|url|website|email/i],
        ['Name', /first.?name|last.?name|full.?name|^name$/i],
        ['Location', /city|state|province|zip|postal|country|location|address|region/i],
        ['Company', /company|organization|employer|business|firm|corp/i],
        ['Company Size', /^size$|employees|headcount|staff/i],
        ['Industry & Revenue', /industry|revenue|sector|vertical|specialt|keyword|niche/i],
        ['Technology', /tech|software|stack|tools|platform/i],
    ];
    for (const [cat, re] of rules) if (re.test(k)) return cat;
    return 'Other';
}

// ── Bulk Edit Modal ──
function BulkEditModal({ open, mode, col, onApply, onClose }) {
    const [text, setText] = useState('');

    if (!open) return null;

    const apply = () => {
        const values = text
            .split(/[\n,]+/)
            .map(v => v.trim())
            .filter(v => v);
        onApply(col, values, mode);
        setText('');
        onClose();
    };

    const isInclude = mode === 'include';

    return (
        <Portal><div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/60 backdrop-blur-sm" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-md p-5 space-y-4 mx-4">
                <div className="flex justify-between items-center">
                    <h3 className="text-base font-bold text-slate-900 dark:text-white">
                        Bulk {isInclude ? 'Include' : 'Exclude'} — {humanize(col)}
                    </h3>
                    <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-4 w-4 text-slate-400" /></button>
                </div>
                <p className="text-sm text-slate-500">
                    Paste values separated by commas or newlines. Each value will be added to the <strong>{isInclude ? 'Includes' : 'Excludes'}</strong> list.
                </p>
                <textarea
                    className="w-full h-36 text-sm p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 resize-none"
                    placeholder="CEO, CTO, Manager, Director..."
                    value={text}
                    onChange={e => setText(e.target.value)}
                />
                <div className="flex gap-2 justify-end">
                    <button className="btn btn-secondary text-sm" onClick={onClose}>Cancel</button>
                    <button className="btn btn-primary text-sm" onClick={apply}>Add Values</button>
                </div>
            </div>
        </div></Portal>
    );
}

// ── Single column filter (Apollo/Pipeline pattern) ──
function FilterColumn({ col, sel, onSet, onClear, onBulkSet }) {
    const [open, setOpen] = useState(false);
    const [incText, setIncText] = useState('');
    const [excText, setExcText] = useState('');
    const [bulkModal, setBulkModal] = useState(null); // { mode: 'include'|'exclude' } or null

    const inc = sel?.include || [];
    const exc = sel?.exclude || [];
    const activeCount = inc.length + exc.length;

    const addInc = () => {
        const v = incText.trim();
        if (!v || inc.includes(v)) { setIncText(''); return; }
        onSet(col, v, 'include');
        setIncText('');
    };
    const addExc = () => {
        const v = excText.trim();
        if (!v || exc.includes(v)) { setExcText(''); return; }
        onSet(col, v, 'exclude');
        setExcText('');
    };
    const keyInc = (e) => { if (e.key === 'Enter') addInc(); };
    const keyExc = (e) => { if (e.key === 'Enter') addExc(); };

    return (
        <div className="border-b border-slate-100 dark:border-slate-800 last:border-b-0">
            {/* Column header */}
            <button
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors"
                onClick={() => setOpen(!open)}
            >
                {open ? <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />}
                <span className="flex-1 text-left truncate">{humanize(col)}</span>
                {activeCount > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-brand-500 text-white font-bold leading-none">{activeCount}</span>
                )}
            </button>

            {open && (
                <div className="px-4 pb-3 space-y-3">
                    {/* ── Includes ── */}
                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400">Includes</span>
                            <button
                                className="text-[11px] text-slate-400 hover:text-brand-500 font-medium"
                                onClick={() => setBulkModal({ mode: 'include' })}
                            >Bulk edit</button>
                        </div>
                        <div className="flex gap-1.5">
                            <input
                                className="flex-1 h-8 px-2.5 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 focus:outline-none focus:ring-1 focus:ring-emerald-500/30 focus:border-emerald-400"
                                placeholder="Add value..."
                                value={incText}
                                onChange={e => setIncText(e.target.value)}
                                onKeyDown={keyInc}
                            />
                            <button
                                className="h-8 w-8 flex items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800 transition-colors shrink-0"
                                onClick={addInc}
                                title="Add include value"
                            ><Plus className="h-3.5 w-3.5" /></button>
                        </div>
                        {inc.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                                {inc.map(v => (
                                    <span key={'i' + v} className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800">
                                        {v}
                                        <button onClick={() => onSet(col, v, null)} className="hover:text-emerald-900 dark:hover:text-emerald-200"><X className="h-3 w-3" /></button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* ── Excludes ── */}
                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[11px] font-bold text-red-500 dark:text-red-400">Excludes</span>
                            <button
                                className="text-[11px] text-slate-400 hover:text-red-500 font-medium"
                                onClick={() => setBulkModal({ mode: 'exclude' })}
                            >Bulk edit</button>
                        </div>
                        <div className="flex gap-1.5">
                            <input
                                className="flex-1 h-8 px-2.5 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 focus:outline-none focus:ring-1 focus:ring-red-500/30 focus:border-red-400"
                                placeholder="Add value..."
                                value={excText}
                                onChange={e => setExcText(e.target.value)}
                                onKeyDown={keyExc}
                            />
                            <button
                                className="h-8 w-8 flex items-center justify-center rounded-lg bg-red-50 text-red-500 border border-red-200 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800 transition-colors shrink-0"
                                onClick={addExc}
                                title="Add exclude value"
                            ><Plus className="h-3.5 w-3.5" /></button>
                        </div>
                        {exc.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                                {exc.map(v => (
                                    <span key={'e' + v} className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
                                        {v}
                                        <button onClick={() => onSet(col, v, null)} className="hover:text-red-900 dark:hover:text-red-200"><X className="h-3 w-3" /></button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {activeCount > 0 && (
                        <button
                            className="text-[11px] text-slate-400 hover:text-red-500 font-medium"
                            onClick={() => onClear(col)}
                        >Remove empty fields</button>
                    )}
                </div>
            )}

            <BulkEditModal
                open={!!bulkModal}
                mode={bulkModal?.mode || 'include'}
                col={col}
                onApply={onBulkSet}
                onClose={() => setBulkModal(null)}
            />
        </div>
    );
}

// ── MAIN ──
export default function LeadManagement() {
    const { runAction } = useAction();
    // Data
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [total, setTotal] = useState(0);
    const [countCapped, setCountCapped] = useState(false);
    // Search params — applied when user clicks "Search Leads". Default = {} shows all leads.
    const [appliedParams, setAppliedParams] = useState({});

    // Draft filters (editing state, not yet searched)
    const [kw, setKw] = useState('');
    const [domain, setDomain] = useState('');
    const [status, setStatus] = useState('');
    const [limit, setLimit] = useState(1000);
    const [colFilters, setColFilters] = useState({});

    // Available columns
    const [cols, setCols] = useState([]);
    const [colsLoading, setColsLoading] = useState(true);

    // Pagination
    const [page, setPage] = useState(1);
    const [ps, setPs] = useState(() => Number(sessionStorage.getItem('leadsPageSize')) || DEFAULT_PAGE_SIZE);

    // Export / JSON
    const [exportOpen, setExportOpen] = useState(false);
    const [jsonOpen, setJsonOpen] = useState(false);
    const [jsonText, setJsonText] = useState('');
    const [jsonErr, setJsonErr] = useState('');
    const [copied, setCopied] = useState(false);

    // Load column names once (keys only, no values — fast)
    useEffect(() => {
        setColsLoading(true);
        apiService.getLeadsFilters().then(r => setCols((r.data.data || []).map(f => f.key))).catch(() => { }).finally(() => setColsLoading(false));
    }, []);

    // Fetch data when appliedParams or pagination changes
    useEffect(() => {
        fetchData();
    }, [page, ps, appliedParams]);

    const fetchData = async () => {
        if (loading) return;
        setLoading(true);
        try {
            const p = { limit: ps, offset: (page - 1) * ps, ...appliedParams };
            const r = await apiService.getLeads(p);
            setRows(r.data.data.emails || r.data.data.items || []);
            setTotal(r.data.data.total || 0);
            setCountCapped(!!r.data.data.countCapped);
        } catch (_) { } finally { setLoading(false); }
    };

    const buildParams = useCallback(() => {
        const p = {};
        if (kw.trim()) p.keywords = kw.trim();
        if (domain.trim()) p.domain = domain.trim().toLowerCase();
        if (status) p.status = status;
        if (Object.keys(colFilters).length > 0) p.filters = JSON.stringify(colFilters);
        if (limit !== 1000) p.maxResults = limit;
        return p;
    }, [kw, domain, status, colFilters, limit]);

    const handleSearch = async () => {
        if (isSearching) return;
        const params = buildParams();
        setAppliedParams(params);
        setPage(1);
        setIsSearching(true);
        await runAction(async () => {
            const p = { limit: ps, offset: 0, ...params };
            const r = await apiService.getLeads(p);
            setRows(r.data.data.emails || r.data.data.items || []);
            setTotal(r.data.data.total || 0);
            setCountCapped(!!r.data.data.countCapped);
        }, { message: 'Searching leads...' });
        setIsSearching(false);
    };
    const handleKeyDown = (e) => { if (e.key === 'Enter') handleSearch(); };

    // ── Single value toggle (include / exclude / remove) ──
    const toggleVal = useCallback((col, val, ns) => {
        setColFilters(prev => {
            const next = { ...prev };
            const ci = next[col]?.include ? [...next[col].include] : [];
            const ce = next[col]?.exclude ? [...next[col].exclude] : [];
            const ii = ci.indexOf(val), ei = ce.indexOf(val);
            if (ii >= 0) ci.splice(ii, 1);
            if (ei >= 0) ce.splice(ei, 1);
            if (ns === 'include') ci.push(val);
            else if (ns === 'exclude') ce.push(val);
            if (ci.length === 0 && ce.length === 0) delete next[col];
            else { next[col] = {}; if (ci.length) next[col].include = ci; if (ce.length) next[col].exclude = ce; }
            return next;
        });
    }, []);

    // ── Bulk set multiple values ──
    const bulkSet = useCallback((col, values, mode) => {
        setColFilters(prev => {
            const next = { ...prev };
            const list = next[col]?.[mode] ? [...next[col][mode]] : [];
            for (const v of values) { if (!list.includes(v)) list.push(v); }
            if (!next[col]) next[col] = {};
            next[col] = { ...next[col], [mode]: list };
            // Keep existing opposite list
            if (mode === 'include' && !next[col].exclude) next[col].exclude = next[col].exclude || [];
            if (mode === 'exclude' && !next[col].include) next[col].include = next[col].include || [];
            return next;
        });
    }, []);

    const clearCol = useCallback((col) => {
        setColFilters(prev => { const n = { ...prev }; delete n[col]; return n; });
    }, []);

    const clearAll = () => {
        setKw(''); setDomain(''); setStatus(''); setColFilters({}); setLimit(1000);
        setAppliedParams({}); setPage(1);
    };

    const hasDraftFilters = !!(kw.trim() || domain.trim() || status || Object.keys(colFilters).length || limit !== 1000);
    const totalActive = Object.values(colFilters).reduce((s, v) => s + (v.include?.length || 0) + (v.exclude?.length || 0), 0);

    const appliedChips = useMemo(() => {
        const c = [];
        if (appliedParams.keywords) c.push({ l: `Search: "${appliedParams.keywords}"`, s: '' });
        if (appliedParams.domain) c.push({ l: `Domain: ${appliedParams.domain}`, s: '' });
        if (appliedParams.status) c.push({ l: `Status: ${appliedParams.status}`, s: '' });
        if (appliedParams.maxResults) c.push({ l: `Max: ${appliedParams.maxResults} leads`, s: '' });
        const f = appliedParams.filters;
        if (f) {
            let parsed = {};
            try { parsed = typeof f === 'string' ? JSON.parse(f) : f; } catch (_) { }
            for (const [k, v] of Object.entries(parsed)) {
                (v?.include || []).forEach(val => c.push({ l: `${humanize(k)}: ${val}`, s: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400' }));
                (v?.exclude || []).forEach(val => c.push({ l: `NOT ${humanize(k)}: ${val}`, s: 'border-red-200 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400' }));
            }
        }
        return c;
    }, [appliedParams]);

    const buildJSON = useCallback(() => {
        const o = { ...buildParams() };
        if (limit !== 1000) o.totalResults = limit;
        if (o.filters) {
            try { o.filters = typeof o.filters === 'string' ? JSON.parse(o.filters) : o.filters; } catch (_) { }
        }
        return JSON.stringify(o, null, 2);
    }, [buildParams, limit]);

    const openJSON = () => { setJsonText(buildJSON()); setJsonErr(''); setCopied(false); setJsonOpen(true); };
    const copyJSON = () => { navigator.clipboard.writeText(buildJSON()); setCopied(true); setTimeout(() => setCopied(false), 2000); };

    const importJSON = () => {
        try {
            const o = JSON.parse(jsonText);
            if (o.keywords) setKw(o.keywords);
            if (o.domain) setDomain(o.domain);
            if (o.status) setStatus(o.status);
            if (o.filters && typeof o.filters === 'object') setColFilters(o.filters);
            if (o.totalResults) setLimit(Number(o.totalResults));
            setJsonOpen(false);
        } catch (e) { setJsonErr('Invalid JSON: ' + e.message); }
    };

    const totalPages = Math.max(1, Math.ceil(total / ps));

    const xtraKeys = useMemo(() => {
        const s = new Set();
        for (const r of rows) {
            if (!r.extra_columns) continue;
            try { const e = typeof r.extra_columns === 'string' ? JSON.parse(r.extra_columns) : r.extra_columns; if (e && typeof e === 'object' && !Array.isArray(e)) Object.keys(e).forEach(k => { if (k !== 'tags' && k !== 'email') s.add(k); }); } catch (_) { }
        }
        return [...s];
    }, [rows]);

    const groupedCols = useMemo(() => {
        const g = {};
        for (const c of cols) { const cat = categorizeKey(c); if (!g[cat]) g[cat] = []; g[cat].push(c); }
        for (const k of Object.keys(g)) g[k].sort();
        return ['Job Title & Role', 'Contact Info', 'Name', 'Location', 'Company', 'Company Size', 'Industry & Revenue', 'Technology', 'Other']
            .filter(x => g[x]).map(x => ({ cat: x, keys: g[x] }));
    }, [cols]);

    const psChange = (n) => { if (!PAGE_SIZE_OPTIONS.includes(n)) return; sessionStorage.setItem('leadsPageSize', String(n)); setPs(n); setPage(1); };

    return (
        <div className="space-y-6 max-w-[1600px] mx-auto">
            {/* JSON Modal */}
            {jsonOpen && (
                <Portal><div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/60 backdrop-blur-sm" onClick={e => e.target === e.currentTarget && setJsonOpen(false)}>
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-lg p-6 space-y-4 mx-4">
                        <div className="flex justify-between items-center">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Filter Preset (JSON)</h3>
                            <button onClick={() => setJsonOpen(false)} className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-5 w-5 text-slate-400" /></button>
                        </div>
                        <p className="text-sm text-slate-500">Copy this JSON to save your filter configuration. Paste someone else's preset and click Import to apply it.</p>
                        <textarea className="w-full h-48 text-sm font-mono p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" value={jsonText} onChange={e => { setJsonText(e.target.value); setJsonErr(''); }} />
                        {jsonErr && <p className="text-sm text-red-500">{jsonErr}</p>}
                        <div className="flex gap-2 justify-end">
                            <button className="btn btn-secondary" onClick={copyJSON}>{copied ? 'Copied!' : 'Copy'} <Copy className="h-4 w-4 ml-1" /></button>
                            <button className="btn btn-primary" onClick={importJSON}>Import Filters</button>
                        </div>
                    </div>
                </div></Portal>
            )}

            <CSVExportModal
                open={exportOpen}
                onClose={() => setExportOpen(false)}
                selectedJob={{ job_id: 'ALL', job_name: 'All Leads' }}
                selectedFilters={{
                    status: appliedParams.status || '',
                    search: appliedParams.keywords || '',
                    domain: appliedParams.domain || '',
                    filters: appliedParams.filters || '',
                    maxResults: appliedParams.maxResults || '',
                }}
            />

            {/* ═══ Header ═══ */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 px-6 py-5">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Lead Management</h1>
                        <p className="text-slate-500 text-sm mt-0.5">Search verified leads by any field. Include what you want, exclude what you don&apos;t.</p>
                    </div>
                    <div className="flex items-center gap-3">
                        {hasDraftFilters && (
                            <button className="btn btn-secondary h-10 text-sm" onClick={clearAll}>
                                <RotateCcw className="h-4 w-4 mr-1" /> Reset All
                            </button>
                        )}
                        <button className="btn btn-secondary h-10 text-sm" onClick={openJSON}>
                            <Copy className="h-4 w-4 mr-1" /> JSON
                        </button>
                        <button
                            className={`btn btn-primary h-12 px-8 text-base font-bold shadow-lg shadow-brand-500/20 ${isSearching ? 'opacity-70 cursor-not-allowed' : ''}`}
                            onClick={handleSearch}
                            disabled={isSearching}
                        >
                            {isSearching ? (
                                <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Searching...</>
                            ) : (
                                <><Search className="h-5 w-5 mr-2" /> Search Leads</>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            <div className="flex gap-6">
                {/* ═══ LEFT: All Filters (unified) ═══ */}
                <div className="w-[340px] shrink-0">
                    <div className="sticky top-4 bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
                        {/* Quick Filters */}
                        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 space-y-3">
                            <h3 className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Quick Filters</h3>
                            <input
                                className="w-full h-10 px-3 text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                                placeholder="Keywords (searches all fields)..."
                                value={kw}
                                onChange={e => setKw(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                            <input
                                className="w-full h-10 px-3 text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                                placeholder="Domain (e.g. gmail.com)"
                                value={domain}
                                onChange={e => setDomain(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                            <div className="flex gap-2">
                                <select className="flex-1 h-10 py-0 px-3 text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" value={status} onChange={e => setStatus(e.target.value)}>
                                    <option value="">All Statuses</option>
                                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                                <select className="w-[120px] h-10 py-0 px-3 text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" value={limit} onChange={e => setLimit(Number(e.target.value))}>
                                    {LIMIT_OPTIONS.map(n => <option key={n} value={n}>{n.toLocaleString()}</option>)}
                                </select>
                            </div>
                        </div>

                        {/* Column Filters */}
                        <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2">
                            <h3 className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                                Column Filters
                            </h3>
                            {totalActive > 0 && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-brand-500 text-white font-bold leading-none">{totalActive}</span>
                            )}
                        </div>

                        <div className="max-h-[calc(100vh-560px)] overflow-y-auto">
                            {colsLoading ? (
                                <div className="flex items-center gap-2 px-5 py-8 text-sm text-slate-400 justify-center">
                                    <Loader2 className="h-4 w-4 animate-spin" /> Loading columns...
                                </div>
                            ) : groupedCols.length === 0 ? (
                                <div className="px-5 py-8 text-sm text-slate-400 text-center leading-relaxed">
                                    No filterable columns detected.<br />
                                    <span className="text-xs text-slate-500">Upload a CSV with columns like &quot;job_title&quot;, &quot;company&quot;, &quot;location&quot;, etc. to enable filtering.</span>
                                </div>
                            ) : (
                                groupedCols.map(group => (
                                    <div key={group.cat}>
                                        <div className="px-5 py-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400 bg-slate-50/50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-800">
                                            {group.cat}
                                        </div>
                                        {group.keys.map(k => (
                                            <FilterColumn
                                                key={k}
                                                col={k}
                                                sel={colFilters[k]}
                                                onSet={toggleVal}
                                                onClear={clearCol}
                                                onBulkSet={bulkSet}
                                            />
                                        ))}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* ═══ RIGHT: Results ═══ */}
                <div className="flex-1 min-w-0">
                    <div className="card">
                        <div className="card-header bg-slate-50/50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-800 px-5 py-3">
                            <div className="flex items-center justify-between flex-wrap gap-3">
                                <div className="flex flex-wrap gap-1.5 items-center">
                                    {appliedChips.length > 0 ? (
                                        appliedChips.map((c, i) => (
                                            <span key={i} className={`inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-full border ${c.s || 'bg-brand-50 text-brand-700 border-brand-200 dark:bg-brand-900/20 dark:text-brand-400 dark:border-brand-800'}`}>
                                                {c.l}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="text-sm text-slate-400">Showing all verified leads — no filters applied.</span>
                                    )}
                                </div>
                                <span className="text-sm text-slate-600 dark:text-slate-300 font-semibold whitespace-nowrap">
                                    <span className="text-lg font-bold text-slate-900 dark:text-white">{total.toLocaleString()}{countCapped ? '+' : ''}</span> leads found
                                </span>
                            </div>
                        </div>

                        <div className="overflow-x-auto relative">
                            {loading && (
                                <div className="absolute top-0 left-0 right-0 h-0.5 bg-brand-100 dark:bg-brand-900/40 overflow-hidden z-10">
                                    <div className="h-full bg-brand-500" style={{ width: '60%', animation: 'scanbar 1.2s ease-in-out infinite' }} />
                                </div>
                            )}
                            <style>{`@keyframes scanbar{0%{transform:translateX(-100%)}100%{transform:translateX(280%)}}`}</style>
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-slate-50 dark:bg-slate-800/50 text-left border-b border-slate-100 dark:border-slate-800">
                                        <th className="px-5 py-3 text-xs font-bold uppercase text-slate-500 w-14">#</th>
                                        <th className="px-5 py-3 text-xs font-bold uppercase text-slate-500">Email</th>
                                        <th className="px-5 py-3 text-xs font-bold uppercase text-slate-500">Status</th>
                                        <th className="px-5 py-3 text-xs font-bold uppercase text-slate-500">Safety</th>
                                        {xtraKeys.slice(0, 8).map(k => (
                                            <th key={k} className="px-5 py-3 text-xs font-bold uppercase text-slate-500 whitespace-nowrap">{humanize(k)}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                    {loading ? (
                                        Array.from({ length: 8 }).map((_, i) => (
                                            <tr key={i} className="border-b border-slate-100 dark:border-slate-800">
                                                <td className="px-5 py-4"><div className="h-4 w-6 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" /></td>
                                                <td className="px-5 py-4"><div className="h-4 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" style={{ width: '160px' }} /></td>
                                                <td className="px-5 py-4"><div className="h-5 w-14 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse" /></td>
                                                <td className="px-5 py-4"><div className="h-4 w-10 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" /></td>
                                            </tr>
                                        ))
                                    ) : rows.length === 0 ? (
                                        <tr>
                                            <td colSpan={4 + Math.min(xtraKeys.length, 8)} className="p-24 text-center">
                                                <div className="max-w-sm mx-auto space-y-3">
                                                    <div className="text-4xl">&#x1F50D;</div>
                                                    <p className="text-slate-500 text-base font-medium">No leads match your filters</p>
                                                    <p className="text-slate-400 text-sm">Try removing some filters or using broader criteria.</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        rows.map((r, i) => {
                                            const [sfLabel, sfCls] = safety(r.status);
                                            return (
                                                <tr key={r.id || i} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                                                    <td className="px-5 py-3.5 text-sm text-slate-400 tabular-nums">{(page - 1) * ps + i + 1}</td>
                                                    <td className="px-5 py-3.5 text-sm font-semibold text-slate-800 dark:text-slate-200">{r.email}</td>
                                                    <td className="px-5 py-3.5">
                                                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase ${statusBadge(r.status)}`}>{r.status}</span>
                                                    </td>
                                                    <td className={`px-5 py-3.5 text-sm font-medium ${sfCls}`}>{sfLabel}</td>
                                                    {xtraKeys.slice(0, 8).map(k => {
                                                        let ed = {};
                                                        if (r.extra_columns) {
                                                            try { ed = typeof r.extra_columns === 'string' ? JSON.parse(r.extra_columns) : r.extra_columns; if (typeof ed !== 'object' || Array.isArray(ed)) ed = {}; } catch (_) { }
                                                        }
                                                        return (
                                                            <td key={k} className="px-5 py-3.5 text-sm text-slate-600 dark:text-slate-300 max-w-[140px] truncate" title={ed[k] ?? ''}>
                                                                {ed[k] ? String(ed[k]) : <span className="text-slate-300 dark:text-slate-600">—</span>}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {total > 0 && (
                            <div className="flex items-center justify-between px-5 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-900/30">
                                <div className="flex items-center gap-3">
                                    <button className="btn btn-primary" onClick={() => setExportOpen(true)}>
                                        <Download className="h-4 w-4 mr-1.5" /> Export Results
                                    </button>
                                </div>
                                <div className={`${loading ? 'opacity-50 pointer-events-none' : ''}`}>
                                    <SmartPagination currentPage={Math.min(page, totalPages)} totalPages={totalPages} totalRows={total} pageSize={ps} onPageChange={setPage} onPageSizeChange={psChange} disabled={loading} />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
